import { Injectable, inject } from '@angular/core';
import { AddressProducts, createAddress, InvoiceListFilter, Rechnung } from '@mwe/models';
import { SessionStorage, SessionStorageService, SignalStateProperty } from '@mwe/utils';
import { isArrayWithMinOneItem } from '@mwe/utils';
import { Observable } from 'rxjs';

const interactiveInvoicesAddressProductsKey = 'interactiveInvoices_addressProducts';
const interactiveInvoicesInvoiceListKey = 'interactiveInvoices_invoiceList';
const interactiveInvoicesSelectedInvoiceKey = 'interactiveInvoices_selectedInvoice';
const interactiveInvoicesSelectedDetailProductKey = 'interactiveInvoices_selectedDetailProduct';
const interactiveInvoicesSelectedProductCategory = 'interactiveInvoices_selectedProductCategory';
const interactiveInvoiceAddressSelectionKey = 'interactiveInvoices_addressSelection';

@Injectable({ providedIn: 'root' })
export class InteractiveInvoicesStateService {
  private storage = inject(SessionStorageService);

  keepSessionStorage = false;

  @SessionStorage(interactiveInvoicesAddressProductsKey)
  private _addressProducts: AddressProducts;

  get addressProducts(): AddressProducts {
    if (!this._addressProducts || !this._addressProducts.address || !isArrayWithMinOneItem(this._addressProducts.products)) {
      return undefined;
    }

    return this._addressProducts;
  }

  set addressProducts(addressProducts: AddressProducts) {
    if (!addressProducts) {
      this.storage.clear(interactiveInvoicesAddressProductsKey);
    } else {
      this._addressProducts = {
        ...addressProducts,
        address: createAddress(addressProducts?.address),
      };
    }
  }

  @SessionStorage(interactiveInvoicesInvoiceListKey)
  private _invoiceList: Rechnung[];

  get invoiceList(): Rechnung[] {
    return this._invoiceList;
  }

  set invoiceList(invoiceList: Rechnung[]) {
    if (!invoiceList) {
      this.storage.clear(interactiveInvoicesInvoiceListKey);
    } else {
      this._invoiceList = invoiceList;
    }
  }

  @SessionStorage(interactiveInvoicesSelectedInvoiceKey)
  private _selectedInvoice: Rechnung;

  get selectedInvoice(): Rechnung {
    return this._selectedInvoice;
  }

  set selectedInvoice(rechnung: Rechnung) {
    if (!rechnung) {
      this.storage.clear(interactiveInvoicesSelectedInvoiceKey);
    } else {
      this._selectedInvoice = rechnung;
    }
  }

  @SessionStorage(interactiveInvoicesSelectedDetailProductKey)
  private _selectedDetailProduct: number;

  get selectedDetailProduct(): number {
    return this._selectedDetailProduct;
  }

  set selectedDetailProduct(productIndex: number) {
    this._selectedDetailProduct = productIndex;
  }

  @SessionStorage(interactiveInvoicesSelectedProductCategory)
  private _selectedProductCategory: InvoiceListFilter;
  get selectedProductCategory(): InvoiceListFilter {
    return this._selectedProductCategory;
  }

  set selectedProductCategory(invoiceList: InvoiceListFilter) {
    if (!invoiceList) {
      this.storage.clear(interactiveInvoicesSelectedProductCategory);
      return;
    }
    this._selectedProductCategory = invoiceList;
  }

  @SessionStorage(interactiveInvoiceAddressSelectionKey)
  private _interactiveInvoiceDirectAddressSelection: boolean;

  @SignalStateProperty()
  get directAddressSelection(): boolean {
    return this._interactiveInvoiceDirectAddressSelection;
  }

  set directAddressSelection(isDirectAddressSelection: boolean) {
    this._interactiveInvoiceDirectAddressSelection = isDirectAddressSelection;
  }

  clear(): void {
    if (this.keepSessionStorage) {
      this.keepSessionStorage = false;
      return;
    }

    this.storage.clear(interactiveInvoicesAddressProductsKey);
    this.storage.clear(interactiveInvoicesInvoiceListKey);
    this.storage.clear(interactiveInvoicesSelectedInvoiceKey);
    this.storage.clear(interactiveInvoicesSelectedDetailProductKey);
    this.storage.clear(interactiveInvoicesSelectedProductCategory);
    this.storage.clear(interactiveInvoiceAddressSelectionKey);
  }

  observeAddressProducts(): Observable<AddressProducts> {
    return this.storage.observe(interactiveInvoicesAddressProductsKey);
  }

  observeInvoiceList(): Observable<Rechnung[]> {
    return this.storage.observe(interactiveInvoicesInvoiceListKey);
  }
}
